import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import { format } from '@/utils/date-fns';
import { getAllResourceBookings } from '@/utils/bookings';
import { endOfWeek, startOfWeek } from 'date-fns';
import { useProfilesStore } from '@/stores/profiles';
import { IDepartment, IImage, IInstitution, IProfile } from '@/interfaces';
import api from '@/api';
import {
  BookingInput,
  BookingResourceBookingSlot,
  Profile_Type,
  ProfileInput,
  ScreenProfile,
} from '@/graphql/generated-types';
import { useInstitutionStore } from '@/stores/institution';
import {
  BookingResourceFragment,
  BookingSlotFragment,
  StandardBookingFragment,
} from '@/graphql/custom-types';

export interface IBookingResource {
  id: string;
  slotted: boolean;
  institution: IInstitution;
  title: string;
  description: string;
  authentication: string;
  coverImage: IImage;
  bookings?: IStandardBooking[];
  bookingSlots?: ISlottedBooking[];
  departments: IDepartment[];
}

export type IStandardBooking = StandardBookingFragment & {
  selectedStartDate: string;
  selectedStartTime: string | null;
  selectedEndDate: string;
  selectedEndTime: string | null;
  title: string;
  resourceId: string | null;
};

export interface ISlottedBooking {
  id: string;
  participants: IProfile[];
  bookingSlot: IBookingSlot;
}

export interface IBookingSlot {
  bookingResourceId: string | null;
  bookingResourceSlotId: string | null;
  date: string;
}

export interface ISlottedWeek {
  dateFrom: string | Date | null;
  dateTo: string | Date | null;
}

export interface IBookingResourcesState {
  all: BookingResourceFragment[];
  newBooking: IStandardBooking;
  newSlottedBooking: ISlottedBooking;
  resourceBookings: any;
  selectedSlottedWeek: ISlottedWeek;
  participating: boolean;
  selectedBookingSlots: BookingSlotFragment[];
  selectedBookingEntry?: IStandardBooking | null;
}

export const useBookingResourcesStore = defineStore('bookingResources', {
  state: (): IBookingResourcesState => ({
    all: [],
    resourceBookings: null,
    selectedSlottedWeek: {
      dateFrom: null,
      dateTo: null,
    },
    participating: false,
    newBooking: {
      __typename: 'BookingSlot',
      id: '',
      participants: [],
      selectedStartDate: '',
      selectedStartTime: null,
      selectedEndDate: '',
      selectedEndTime: null,
      title: '',
      resourceId: null,
      description: '',
      start: '',
      end: '',
    },
    selectedBookingEntry: null,
    selectedBookingSlots: [],
    newSlottedBooking: {
      id: '',
      participants: [],
      bookingSlot: {
        bookingResourceId: null,
        bookingResourceSlotId: null,
        date: '',
      },
    },
  }),
  actions: {
    async getBookingResources() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      try {
        const resources = await api.booking.getBookingResources(
          departmentStore.id
        );
        this.all = resources;
        this.resourceBookings = getAllResourceBookings(resources);
      } catch (error) {
        console.log('Error getting bookings', error);
      }
    },
    async getBookingSlots({
      id,
      dateFrom,
      dateTo,
    }: {
      id: string;
      dateFrom: string;
      dateTo: string;
    }) {
      try {
        this.selectedBookingSlots = await api.booking.getBookingSlots(
          id,
          dateFrom,
          dateTo
        );
      } catch (error) {
        console.log(`Error getting bookingSlots from id ${id}`, error);
      }
    },
    setSelectedBookingEntry(bookingEntry?: StandardBookingFragment) {
      this.selectedBookingEntry = bookingEntry as IStandardBooking;
    },
    setSelectedSlottedWeek(week: ISlottedWeek) {
      if (!week) {
        const currentWeek = {
          dateFrom: startOfWeek(new Date(), { weekStartsOn: 1 }),
          dateTo: endOfWeek(new Date(), { weekStartsOn: 1 }),
        };

        this.selectedSlottedWeek = currentWeek;
        return;
      }
      this.selectedSlottedWeek = week;
    },
    setNewBooking(booking: any) {
      this.$patch({
        newBooking: {
          ...this.newBooking,
          ...booking,
        },
      });
    },
    setNewSlottedBooking(booking: ISlottedBooking) {
      this.newSlottedBooking = booking;
    },
    async createNewBooking() {
      const participants = (this.newBooking?.participants || [])
        .filter((item) => item.__typename !== 'Relative')
        .map((participant) => {
          return {
            id: participant.id,
            type: participant.__typename.toUpperCase() as Profile_Type,
          } as ProfileInput;
        });

      const now = new Date();
      const timezone = format(now, 'XXX');
      const end = `${this.newBooking.selectedEndDate}T${this.newBooking.selectedEndTime}:00${timezone}`;
      const start = `${this.newBooking.selectedStartDate}T${this.newBooking.selectedStartTime}:00${timezone}`;
      const institutionStore = useInstitutionStore();

      const booking: BookingInput = {
        bookingResourceId: this.newBooking.resourceId!,
        end,
        start,
        title: this.newBooking.title,
        institutionId: institutionStore.id,
        status: 1, // It should be active
      };

      const response = await api.booking.bookResourceScreen(
        participants,
        booking
      );

      if (!response.id) {
        throw new Error('Error creating booking. No bookingId provided');
      }

      return response.id;
    },
    async setParticipating(participating: boolean) {
      this.participating = participating;
    },
    async createNewSlottedBooking(
      id: string,
      type: Profile_Type,
      slot: BookingResourceBookingSlot
    ) {
      const profilesStore = useProfilesStore();

      await api.booking
        .bookSlottedResource(
          [
            {
              id,
              type,
            },
          ],
          slot
        )
        .then(() => {
          profilesStore.setPinValidation({});
        })
        .catch((error) => {
          throw new Error(`Error creating slotted booking', ${error}`);
        });
    },
    async cancelSlottedBooking(
      profile: ScreenProfile,
      slot: BookingResourceBookingSlot
    ) {
      const profilesStore = useProfilesStore();

      await api.booking
        .cancelSlottedBooking(profile, slot)
        .then(() => {
          profilesStore.setPinValidation({});
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });

          throw new Error('Error cancelling booking', error);
        });
    },
  },
});
